import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import Logo from "@/components/Logo";

const GEO = "https://images.pexels.com/photos/13336520/pexels-photo-13336520.png";

function AuthShell({ children, title, subtitle }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      <div className="hidden lg:block relative bg-navy-900 overflow-hidden">
        <img src={GEO} alt="Gold geometry" className="absolute inset-0 h-full w-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy-900 via-navy-900/40 to-transparent" />
        <div className="relative h-full flex flex-col justify-between p-12">
          <Link to="/" data-testid="auth-logo">
            <Logo variant="light" />
          </Link>
          <div>
            <div className="font-mono uppercase tracking-[0.2em] text-gold-400 text-[0.6rem] mb-5">Managing Disaster Australia · Private system</div>
            <h2 className="font-heading font-light text-white text-4xl leading-[1.1] tracking-tight">
              The office where positive outcomes become <span className="ed-italic text-gold-400">repeatable.</span>
            </h2>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-navy-700/60 hover:text-navy-900 mb-8" data-testid="auth-back">
            <ArrowLeft className="h-4 w-4" /> Back
          </Link>
          <h1 className="font-heading font-light text-4xl text-navy-900 tracking-tight">{title}</h1>
          <p className="text-navy-900/55 mt-3 font-light">{subtitle}</p>
          {children}
        </div>
      </div>
    </div>
  );
}

export function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      nav("/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Welcome back" subtitle="Sign in to The MDAu Office.">
      <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-sm" data-testid="login-error">
            {error}
          </div>
        )}
        <Field label="Email" type="email" value={email} onChange={setEmail} testid="login-email" />
        <Field label="Password" type="password" value={password} onChange={setPassword} testid="login-password" />
        <button
          type="submit"
          disabled={loading}
          data-testid="login-submit-button"
          className="w-full bg-navy-900 text-gold-400 font-semibold py-3.5 rounded-sm hover:bg-navy-800 transition-colors flex items-center justify-center gap-2 disabled:opacity-60 mt-2"
        >
          {loading && <Loader2 className="h-4 w-4 animate-spin" />} Sign in
        </button>
        <p className="text-sm text-navy-700/60 text-center">
          Have an invite code?{" "}
          <Link to="/register" className="text-navy-900 font-semibold hover:text-gold" data-testid="to-register-link">Register</Link>
        </p>
        <div className="bg-offwhite border border-[#E2E8F0] rounded-sm p-4 text-xs text-navy-700/70 label-mono leading-relaxed flex items-center gap-2">
          <Lock className="h-3.5 w-3.5 shrink-0" /> Private system — access restricted to authorised personnel.
        </div>
      </form>
    </AuthShell>
  );
}

export function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", company: "", password: "", invite_code: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register(form);
      nav("/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Create your account" subtitle="Registration is invite-only. An invite code from the administrator is required.">
      <form onSubmit={submit} className="mt-8 space-y-5" data-testid="register-form">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-sm" data-testid="register-error">
            {error}
          </div>
        )}
        <Field label="Invite code" value={form.invite_code} onChange={set("invite_code")} testid="register-invite-code" />
        <Field label="Full name" value={form.name} onChange={set("name")} testid="register-name" />
        <Field label="Company" value={form.company} onChange={set("company")} testid="register-company" />
        <Field label="Email" type="email" value={form.email} onChange={set("email")} testid="register-email" />
        <Field label="Password" type="password" value={form.password} onChange={set("password")} testid="register-password" />
        <button
          type="submit"
          disabled={loading}
          data-testid="register-submit-button"
          className="w-full bg-gold text-navy-900 font-bold py-3.5 rounded-sm hover:bg-gold-light transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {loading && <Loader2 className="h-4 w-4 animate-spin" />} Create account
        </button>
        <p className="text-sm text-navy-700/60 text-center">
          Already registered?{" "}
          <Link to="/login" className="text-navy-900 font-semibold hover:text-gold" data-testid="to-login-link">Sign in</Link>
        </p>
      </form>
    </AuthShell>
  );
}

function Field({ label, type = "text", value, onChange, testid }) {
  return (
    <div>
      <label className="font-mono uppercase tracking-[0.18em] text-navy-900/60 text-[0.65rem] block mb-1">{label}</label>
      <input
        type={type}
        required
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="draft-input w-full text-navy-900"
      />
    </div>
  );
}
